import React from "react";
// import { Box, Typography } from "@mui/material";
import ImageUploadField from "../../../../components/ImageUploadField";
import Field from "../shared/Field";
import SummaryCard from "../shared/SummaryCard";
import FormFeatures from "../forms/FormFeatures";
import FormTeam from "../forms/FormTeam";
import FormPricing from "../forms/FormPricing";
import FormTestimonials from "../forms/FormTestimonials";
import { VALIDATION_MAP } from "../utils/validationUtils";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

// ─────────────────────────────────────────────────────────────────
// HOW renderSummary WORKS NOW:
//
// renderSummary(item, onClick, onDelete) receives all three args.
// It returns a <SummaryCard> which handles:
//   - card border / hover / background
//   - edit + delete icon buttons (top-right, appear on hover)
//   - click propagation guard on controls
//
// You only write the inner content. No more copy-pasted sx objects.
// ─────────────────────────────────────────────────────────────────

export const OVERVIEW_CONFIGS = {
  // ── Features ──────────────────────────────────────────────────
  features: {
    icon: "⚡",
    label: "Features",
    itemLabel: "Feature",
    itemsKey: "items",
    emptyItem: () => ({ icon: "", title: "", description: "", tags: "" }),
    isValid: VALIDATION_MAP.features,
    getDraftLabel: (d) => d?.title,
    countLabel: (items) =>
      `${items.filter((i) => i.title).length} features added`,
    extraFields: (data, onChange) => (
      <Field
        label="Section Title"
        value={data.sectionTitle}
        onChange={(v) => onChange({ ...data, sectionTitle: v })}
      />
    ),
    renderSummary: (item, onClick, onDelete) => (
      <SummaryCard onClick={onClick} onDelete={onDelete} width={200}>
        {item.icon ? (
          <Box
            component="img"
            src={item.icon}
            sx={{ width: 36, height: 36, borderRadius: "8px", mb: 1 }}
          />
        ) : (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              background: "rgba(59,130,246,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <Typography fontSize={18}>⚡</Typography>
          </Box>
        )}
        <Typography variant="subtitle2" fontWeight={600} noWrap mb={0.5}>
          {item.title || "Untitled Feature"}
        </Typography>
        {item.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {item.description}
          </Typography>
        )}
        {item.tags && (
          <Typography
            variant="caption"
            color="#3b82f6"
            sx={{ fontSize: "9px", display: "block", mt: 0.5 }}
          >
            {item.tags}
          </Typography>
        )}
      </SummaryCard>
    ),
    renderForm: (draft, setDraft) => (
      <FormFeatures draft={draft} setDraft={setDraft} />
    ),
  },

  // ── Team ──────────────────────────────────────────────────────
  team: {
    icon: "👥",
    label: "Team",
    itemLabel: "Member",
    itemsKey: "items",
    emptyItem: () => ({
      name: "",
      role: "",
      photoUrl: "",
      experience: "",
      skills: [],
    }),
    isValid: (d) => !!d?.name,
    getDraftLabel: (d) => d?.name,
    countLabel: (items) =>
      `${items.filter((i) => i.name).length} members added`,
    extraFields: (data, onChange) => (
      <>
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
          <Field
            label="Section Heading"
            value={data.sectionTitle}
            onChange={(v) => onChange({ ...data, sectionTitle: v })}
            placeholder="Our MERN Stack Experts"
          />
          <Field
            label="Section Subheading"
            value={data.subheading}
            onChange={(v) => onChange({ ...data, subheading: v })}
            placeholder="Backed by 5+ in-house team members..."
          />
        </Box>

        <FormControl size="small" fullWidth sx={{ mb: 1 }}>
          <InputLabel>Card Design Style</InputLabel>
          <Select
            value={data.cardStyle || "overlap"}
            label="Card Design Style"
            onChange={(e) => onChange({ ...data, cardStyle: e.target.value })}
            sx={{ borderRadius: "8px" }}
          >
            <MenuItem value="overlap">
              Overlap Style (circle avatar overlapping card)
            </MenuItem>
            <MenuItem value="flat">Flat Style (top image, no overlap)</MenuItem>
          </Select>
        </FormControl>
      </>
    ),
    renderSummary: (item, onClick, onDelete) => (
      <SummaryCard onClick={onClick} onDelete={onDelete} width={180}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
          {item.photoUrl ? (
            <Box
              component="img"
              src={item.photoUrl}
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid rgba(59,130,246,0.3)",
              }}
            />
          ) : (
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                background: "rgba(59,130,246,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid rgba(59,130,246,0.2)",
              }}
            >
              <Typography fontSize={28}>👤</Typography>
            </Box>
          )}

          <Box textAlign="center">
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {item.name || "Unnamed"}
            </Typography>
            {item.role && (
              <Typography
                variant="caption"
                color="#3b82f6"
                display="block"
                noWrap
              >
                {item.role}
              </Typography>
            )}
            {item.experience && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {item.experience}
              </Typography>
            )}
          </Box>

          {item.skills?.length > 0 && (
            <Box
              display="flex"
              flexWrap="wrap"
              gap={0.5}
              justifyContent="center"
            >
              {item.skills.slice(0, 3).map((skill, idx) => (
                <Box
                  key={idx}
                  sx={{
                    fontSize: "9px",
                    px: 1,
                    py: 0.2,
                    borderRadius: "4px",
                    background: skill.color || "rgba(59,130,246,0.1)",
                    color: "#3b82f6",
                  }}
                >
                  {skill.name}
                </Box>
              ))}
              {item.skills.length > 3 && (
                <Box
                  sx={{
                    fontSize: "9px",
                    px: 1,
                    py: 0.2,
                    borderRadius: "4px",
                    background: "rgba(255,255,255,0.08)",
                    color: "text.secondary",
                  }}
                >
                  +{item.skills.length - 3}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </SummaryCard>
    ),
    renderForm: (draft, setDraft) => (
      <FormTeam draft={draft} setDraft={setDraft} />
    ),
  },

  // ── Pricing ───────────────────────────────────────────────────
  pricing: {
    icon: "💰",
    label: "Pricing",
    itemLabel: "Package",
    itemsKey: "packages",
    emptyItem: () => ({
      name: "",
      badge: "",
      price: "",
      priceType: "fixed",
      highlighted: false,
      features: [""],
      ctaText: "Get Started",
      ctaUrl: "",
    }),
    isValid: VALIDATION_MAP.pricing,
    getDraftLabel: (d) => d?.name,
    countLabel: (items) =>
      `${items.filter((i) => i.name).length} packages added`,
    extraFields: (data, onChange) => (
      <>
        <Field
          label="Section Title"
          value={data.sectionTitle}
          onChange={(v) => onChange({ ...data, sectionTitle: v })}
        />
        <Field
          label="Section Subheading"
          value={data.subheading}
          onChange={(v) => onChange({ ...data, subheading: v })}
          placeholder="Choose a plan..."
        />
      </>
    ),
    renderSummary: (item, onClick, onDelete) => (
      <SummaryCard
        onClick={onClick}
        onDelete={onDelete}
        width={200}
        // Highlighted packages get a blue border override
        // sx={item.highlighted ? { border: "2px solid #3b82f6 !important" } : {}}
        highlighted={item.highlighted}
      >
        {item.badge && (
          <Box
            sx={{
              display: "inline-block",
              background: "rgba(59,130,246,0.15)",
              color: "#3b82f6",
              fontSize: "9px",
              px: 1,
              py: 0.3,
              borderRadius: "4px",
              fontWeight: 600,
              mb: 1,
            }}
          >
            {item.badge}
          </Box>
        )}
        <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
          {item.name || "Package"}
        </Typography>
        <Typography variant="h6" fontWeight={700} color="#3b82f6" mb={0.25}>
          {item.price}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {item.priceType === "fixed" ? "Fixed Price" : "Custom Quote"}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mt={0.5}
        >
          {item.features?.length || 0} features included
        </Typography>
      </SummaryCard>
    ),
    renderForm: (draft, setDraft) => (
      <FormPricing draft={draft} setDraft={setDraft} />
    ),
  },

  // ── FAQ ───────────────────────────────────────────────────────
  faq: {
    icon: "❓",
    label: "FAQ",
    itemLabel: "Question",
    itemsKey: "items",
    emptyItem: () => ({ question: "", answer: "" }),
    isValid: (d) => !!d?.question,
    getDraftLabel: (d) => d?.question,
    countLabel: (items) =>
      `${items.filter((i) => i.question).length} FAQs added`,
    extraFields: (data, onChange) => (
      <>
        <Field
          label="Section Title"
          value={data.sectionTitle}
          onChange={(v) => onChange({ ...data, sectionTitle: v })}
        />
        <ImageUploadField
          label="Illustration Image (left side)"
          value={data.imageUrl}
          onChange={(v) => onChange({ ...data, imageUrl: v })}
        />
      </>
    ),
    renderSummary: (item, onClick, onDelete) => (
      <SummaryCard onClick={onClick} onDelete={onDelete} width={240}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: "rgba(59,130,246,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1,
          }}
        >
          <Typography fontSize={14}>❓</Typography>
        </Box>
        <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
          {item.question || "Untitled Question"}
        </Typography>
        {item.answer && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {item.answer}
          </Typography>
        )}
      </SummaryCard>
    ),
    renderForm: (draft, setDraft) => (
      <Box>
        <Field
          label="Question"
          value={draft.question}
          onChange={(v) => setDraft((p) => ({ ...p, question: v }))}
          placeholder="What type of applications do you develop?"
        />
        <Field
          label="Answer"
          value={draft.answer}
          onChange={(v) => setDraft((p) => ({ ...p, answer: v }))}
          multiline
          placeholder="We build web applications for e-commerce, SaaS..."
        />
      </Box>
    ),
  },

  // ── Case Studies ──────────────────────────────────────────────
  caseStudies: {
    icon: "💼",
    label: "Case Studies",
    itemLabel: "Project",
    itemsKey: "items",
    emptyItem: () => ({
      title: "",
      category: "",
      siteUrl: "",
      thumbnailUrl: "",
    }),
    isValid: (d) => !!d?.title,
    getDraftLabel: (d) => d?.title,
    countLabel: (items) =>
      `${items.filter((i) => i.title).length} projects added`,
    extraFields: (data, onChange) => (
      <Field
        label="Section Title"
        value={data.sectionTitle}
        onChange={(v) => onChange({ ...data, sectionTitle: v })}
      />
    ),
    renderSummary: (item, onClick, onDelete) => (
      <SummaryCard onClick={onClick} onDelete={onDelete} width={220}>
        {item.thumbnailUrl ? (
          <Box
            component="img"
            src={item.thumbnailUrl}
            sx={{
              width: "100%",
              height: 110,
              borderRadius: "8px",
              objectFit: "cover",
              mb: 1,
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: 110,
              borderRadius: "8px",
              background: "rgba(59,130,246,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <Typography fontSize={28}>💼</Typography>
          </Box>
        )}
        <Typography variant="subtitle2" fontWeight={600} noWrap mb={0.25}>
          {item.title || "Untitled"}
        </Typography>
        {item.category && (
          <Typography variant="caption" color="#3b82f6" display="block" noWrap>
            {item.category}
          </Typography>
        )}
        {item.slug && (
          <Typography
            variant="caption"
            color="success.main"
            sx={{ fontSize: "9px", display: "block", mt: 0.5 }}
          >
            ✓ /case/{item.slug}
          </Typography>
        )}
      </SummaryCard>
    ),
    renderForm: (draft, setDraft) => (
      <Box>
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <Field
            label="Project Title"
            value={draft.title}
            onChange={(v) => setDraft((p) => ({ ...p, title: v }))}
            placeholder="Embracing Nature School"
          />
          <Field
            label="Category"
            value={draft.category}
            onChange={(v) => setDraft((p) => ({ ...p, category: v }))}
            placeholder="E-Commerce Platform"
          />
        </Box>
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <Field
            label="Site URL"
            value={draft.siteUrl}
            onChange={(v) => setDraft((p) => ({ ...p, siteUrl: v }))}
            placeholder="https://example.com"
          />
          <ImageUploadField
            label="Thumbnail"
            value={draft.thumbnailUrl}
            onChange={(v) => setDraft((p) => ({ ...p, thumbnailUrl: v }))}
          />
        </Box>
      </Box>
    ),
  },

  // ── Testimonials ──────────────────────────────────────────────
  testimonials: {
    icon: "💬",
    label: "Testimonials",
    itemLabel: "Testimonial",
    itemsKey: "items",
    emptyItem: () => ({
      quote: "",
      reviewerName: "",
      reviewerRole: "",
      reviewerPhoto: "",
    }),
    isValid: VALIDATION_MAP.testimonials,
    getDraftLabel: (d) => d?.reviewerName,
    countLabel: (items) =>
      `${items.filter((i) => i.quote).length} testimonials added`,
    extraFields: (data, onChange) => (
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
        <Field
          label="Happy Clients Count"
          value={data.clientCount}
          onChange={(v) => onChange({ ...data, clientCount: v })}
          placeholder="10,000+"
        />
        <Field
          label="Tagline"
          value={data.tagline}
          onChange={(v) => onChange({ ...data, tagline: v })}
          placeholder="MERN Developers Trust Us"
        />
      </Box>
    ),
    renderSummary: (item, onClick, onDelete) => (
      <SummaryCard onClick={onClick} onDelete={onDelete} width={260}>
        {/* Opening quote mark */}
        <Typography fontSize={28} color="#3b82f6" lineHeight={1} mb={0.5}>
          "
        </Typography>

        <Typography
          variant="body2"
          mb={1.5}
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {item.quote || "No quote"}
        </Typography>

        <Box display="flex" alignItems="center" gap={1}>
          {item.reviewerPhoto ? (
            <Box
              component="img"
              src={item.reviewerPhoto}
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
          ) : (
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "rgba(59,130,246,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography fontSize={16}>👤</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {item.reviewerName || "Anonymous"}
            </Typography>
            {item.reviewerRole && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {item.reviewerRole}
              </Typography>
            )}
          </Box>
        </Box>
      </SummaryCard>
    ),
    renderForm: (draft, setDraft) => (
      <FormTestimonials draft={draft} setDraft={setDraft} />
    ),
  },
};
